import Link from "next/link";
import React, { useEffect, useMemo, useState } from "react";
import { Wine } from "../../../../context/wineContext/reducer";
import { Producer } from "../../../../context/articlesContext/reducer";
import { numberFormattedWithCurrency } from "../../../../utils/format";
import { useRouter } from "next/router";

interface WineDetailsProps {
  baseWine: { 
    producer: Producer;
    region_1?: string;
    color?: string;
    wines: {
      wine: Wine
    }[],
    base_wine_prices: {
      price: string;
      vintage: number;
    }[],
    grape_variety: string;
    grape_variety_vintage: number;
  };
  wineRegion1: string;
  wineRegion2: string;
};

const WineDetails = ({
  baseWine,
  wineRegion1,
  wineRegion2
}: WineDetailsProps) => {
  const { producer, region_1, color, wines } = baseWine;
  const [baseWinePrice, setBaseWinePrice] = useState<{price: string, vintage: number}>();
  const [varietal, setVarietal] = useState<string>("");
  const [price, setPrice] = useState<string>("0");
  const [grapeVarietyVintage, setGrapeVarietyVintage] = useState<number>(0);
  const router = useRouter();

  useEffect(() => {
    setGrapeVarietyVintage(0);
    const vintageParam = router.query.vintage;
    
    const vintage = vintageParam ? parseInt(vintageParam[0]) : 0;

    if (vintage === 0) {
      baseWine?.grape_variety_vintage && setGrapeVarietyVintage(baseWine.grape_variety_vintage);
      setVarietal(baseWine.grape_variety);
      return;
    }
    const { wine } = wines.find(wine => wine.wine.vintage === vintage);

    if (wine?.grape_variety.length > 0){
      setVarietal(wine.grape_variety)
    }
    else if (baseWine?.grape_variety.length > 0){
      setGrapeVarietyVintage(baseWine.grape_variety_vintage);
      setVarietal(baseWine.grape_variety);
    } 
    
  }, [router.query.vintage]);

  useEffect(() => {
    setBaseWinePrice(undefined);
    const vintageParam = router.query.vintage;
    const vintage = vintageParam ? parseInt(vintageParam[0]) : 0;
    
    if (vintage === 0 && baseWine?.base_wine_prices?.length > 0) {
      // display the price for the most recent (largest) vintage and display it with (YEAR)
      const baseWinePrice = baseWine.base_wine_prices.reduce((prev, current) => (prev.vintage > current.vintage) ? prev : current);
      setBaseWinePrice(baseWinePrice);
      setPrice(baseWinePrice.price);
      return;
    }
  
    if (baseWine?.base_wine_prices?.length > 0 && process.env.NEXT_PUBLIC_BASE_WINE_PRICE_ENABLED === "true") {
      const baseWinePrice = baseWine.base_wine_prices.find(
        (baseWinePrice) => baseWinePrice.vintage === vintage || baseWinePrice.vintage < vintage
      );
      if (parseInt(baseWinePrice?.price) <= 10) {
        setPrice("0")
        return;
      }
       
      if (baseWinePrice && baseWinePrice.vintage !== vintage && vintage > 0) setBaseWinePrice(baseWinePrice);
      setPrice(baseWinePrice?.price || "0");
    } else {
      setPrice("0");
    }
  }, [router.query.vintage]);

  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-1">
      <div>
        <div className="text-sm">
          Producer
        </div>
       {producer && <p className="text-red">
          <Link href={`/producers/` + producer.slug}>{ producer.producer_name }</Link>
        </p>}
      </div>

      <div>
      {parseInt(price) > 0 && (
          <>
            <div className="text-sm">Release Price</div>
            <p className="text-red">
              {numberFormattedWithCurrency("$", parseInt(price))}{" "}
              {baseWinePrice && `(${baseWinePrice.vintage})`}
            </p>
          </>
        )}
      </div>

      <div>
        <div className="text-sm">
          Place of Origin
        </div>
        <p className="text-red">
          {producer && producer?.country }
        </p>
        
        <p className="font-light text-sm opacity-70">
          { wineRegion1 }
        </p>
        <p className="font-light text-sm opacity-70">
          { wineRegion2 }
        </p>
      </div>

      <div>
        <div className="text-sm">
          Color
        </div>
        <p className="text-red">
          { color }
        </p>
      </div>

      <div>
        <div className="text-sm">
          Grape/Blend
        </div>
        <p className="font-light">
          { varietal } { grapeVarietyVintage > 0 && `(${grapeVarietyVintage} vintage)` }
        </p>
      </div>
    </div>
  )
}

export default WineDetails;
